import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {EmployeeFormSections} from '@core/components';
import {AttachmentService, EmployeeService, GroupService} from '@core/services';
import {CompanyStateService} from '@core/states';
import {ModalService} from '@core/services/modal.service';
import {EMPLOYEE_ATTACHMENT_RELATED_TYPE, EMPLOYEE_ROLE_OPTIONS} from '@core/utils';
@Component({
  selector: 'core-modal-add-employee',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    EmployeeFormSections
  ],
  templateUrl: './add-employee.html',
  styleUrl: './add-employee.scss',
  host: {
    style: 'width: 100%;display: flex;flex-direction: column;gap: 1.25rem;'
  }
})
export class AddEmployee implements OnInit, OnDestroy {
  form: FormGroup;
  roleOptions = EMPLOYEE_ROLE_OPTIONS;
  groupOptions: {label: string; value: string | number}[] = [];
  isLoadingOptions = false;
  isSubmitting = false;
  avatarFile: File | null = null;
  avatarPreviewUrl = '';
  contractFile: File | null = null;
  pendingAttachments: File[] = [];
  errorMessage = '';
  private createdEmployeeId: string | number | null = null;
  private modal = inject(ModalService);
  private employeeService = inject(EmployeeService);
  private groupService = inject(GroupService);
  private attachmentService = inject(AttachmentService);
  private companyState = inject(CompanyStateService);
  private formBuilder = inject(FormBuilder);

  constructor() {
    this.form = this.formBuilder.group({
      fullName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      position: [null],
      groupId: [null],
      isGroupManager: [false, Validators.required],
      hiredAt: [''],
      activityKey: ['no'],
      sensitiveData: ['no'],
      cashControl: ['no'],
    });
  }

  ngOnInit() {
    this.loadOptions();
  }

  ngOnDestroy() {
    this.revokeAvatarPreview();
  }

  async submit() {
    if (this.isSubmitting) return;
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.form.disable({emitEvent: false});

    const value = this.form.getRawValue();
    let employeeId = this.createdEmployeeId;
    const {firstName, lastName} = this.splitName(value.fullName || '');
    const payload = {
      firstName,
      lastName,
      email: value.email,
      hiredAt: value.hiredAt || undefined,
      groupId: value.groupId || undefined,
      isGroupManager: !!value.isGroupManager,
      metadata: {
        position: value.position || undefined,
        contractFileName: this.contractFile?.name || undefined,
        activityKey: value.activityKey,
        sensitiveData: value.sensitiveData,
        cashControl: value.cashControl
      }
    };

    if (!employeeId) {
      const response = await this.employeeService.createEmployee(payload, {
        skipLoader: true
      });

      if (!response.success || !response.result?.id) {
        this.isSubmitting = false;
        this.form.enable({emitEvent: false});
        this.errorMessage = response.message || 'No se pudo crear el empleado.';
        return;
      }

      employeeId = response.result.id;
      this.createdEmployeeId = employeeId;
    } else {
      const updateResponse = await this.employeeService.updateEmployee(employeeId, payload, {
        skipLoader: true
      });

      if (!updateResponse.success) {
        this.isSubmitting = false;
        this.form.enable({emitEvent: false});
        this.errorMessage = updateResponse.message || 'No se pudo completar la actualización del empleado.';
        return;
      }
    }

    if (value.isGroupManager && value.groupId && employeeId) {
      const managerResponse = await this.groupService.setManager(value.groupId, employeeId, {
        skipLoader: true
      });
      if (!managerResponse.success) {
        this.isSubmitting = false;
        this.form.enable({emitEvent: false});
        this.errorMessage = managerResponse.message || 'Se creó el empleado, pero no se pudo asignar como responsable del grupo.';
        return;
      }
    }

    const uploadResult = await this.uploadSelectedDocuments(employeeId);
    if (!uploadResult.success) {
      this.isSubmitting = false;
      this.form.enable({emitEvent: false});
      this.errorMessage = uploadResult.message || 'Se creó el empleado, pero no se pudieron subir los documentos.';
      return;
    }

    this.isSubmitting = false;
    this.modal.closeTop(true);
  }

  close() {
    this.modal.closeTop(false);
  }

  openAvatarPicker() {
    if (this.isSubmitting) return;
    const input = document.getElementById('employee-avatar-input') as HTMLInputElement | null;
    input?.click();
  }

  handleAvatarSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    this.avatarFile = file;
    this.setAvatarPreview(file);
    input.value = '';
  }

  get contractFileName() {
    return this.contractFile?.name || '';
  }

  handleContractSelected(file: File | null) {
    this.contractFile = file;
  }

  handleDocumentsSelected(files: File[]) {
    this.pendingAttachments = [...this.pendingAttachments, ...files];
  }

  removePendingAttachment(index: number) {
    this.pendingAttachments = this.pendingAttachments.filter((_, currentIndex) => currentIndex !== index);
  }

  private splitName(fullName: string) {
    const trimmed = fullName.trim();
    if (!trimmed) {
      return {firstName: '', lastName: ''};
    }

    const parts = trimmed.split(' ');
    const firstName = parts.shift() || '';
    const lastName = parts.join(' ');
    return {firstName, lastName};
  }

  private async loadOptions() {
    this.isLoadingOptions = true;
    this.errorMessage = '';

    const groupsResponse = await this.groupService.getGroups({skipLoader: true});

    if (groupsResponse.success && groupsResponse.result) {
      this.groupOptions = groupsResponse.result.map(group => ({
        label: group.name,
        value: group.id
      }));
    }

    if (!groupsResponse.success) {
      this.errorMessage = 'No se pudieron cargar las opciones del formulario.';
    }

    this.isLoadingOptions = false;
  }

  private async uploadSelectedDocuments(employeeId: string | number): Promise<{success: boolean; message?: string}> {
    const files = [this.avatarFile, this.contractFile, ...this.pendingAttachments].filter(Boolean) as File[];
    if (!files.length) {
      return {success: true};
    }

    const companyId = this.companyState.membership()?.company?.id;
    if (!companyId) {
      return {success: false, message: 'No se pudo identificar la empresa para subir los documentos.'};
    }

    const response = await this.attachmentService.uploadAttachments({
      companyId: String(companyId),
      files,
      relatedToType: EMPLOYEE_ATTACHMENT_RELATED_TYPE,
      relatedToId: employeeId,
      skipLoader: true
    });

    if (!response.success) {
      return {success: false, message: response.message};
    }

    this.avatarFile = null;
    this.revokeAvatarPreview();
    this.contractFile = null;
    this.pendingAttachments = [];
    return {success: true};
  }

  private setAvatarPreview(file: File | null) {
    this.revokeAvatarPreview();
    if (!file) {
      this.avatarPreviewUrl = '';
      return;
    }

    this.avatarPreviewUrl = URL.createObjectURL(file);
  }

  private revokeAvatarPreview() {
    if (!this.avatarPreviewUrl) return;
    URL.revokeObjectURL(this.avatarPreviewUrl);
    this.avatarPreviewUrl = '';
  }
}
